import React from 'react';
import { motion } from 'framer-motion';
// Import the CORRECT Lottie component
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { FaAward, FaClock, FaHandshake, FaHeart } from 'react-icons/fa';

// Define the .lottie URL for the story animation
const storyAnimationUrl = "https://lottie.host/82dc921a-3cea-4e5b-9951-2e9a7624219e/1ijTuAe2dL.lottie";

// Animation Variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const textBlockAnimation = { // New variant for the text block
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }
};

const underlineAnimation = { // New variant for the underline
    hidden: { width: 0 },
    visible: { width: "5rem", transition: { duration: 0.6, delay: 0.4 } } // Delay slightly after text
};


const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.2, delayChildren: 0.1 }
    }
};
const itemVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

// ValueCard Component
const ValueCard = ({ icon, title, text }) => (
    <motion.div
        variants={itemVariant}
        className="bg-white p-6 rounded-lg shadow-md text-center border border-gray-100 flex flex-col items-center h-full"
    >
        <div className="text-arcdom-gold text-4xl mb-4">{icon}</div>
        <h4 className="text-lg font-semibold text-arcdom-navy mb-2">{title}</h4>
        <p className="text-sm text-gray-600 leading-relaxed">{text}</p>
    </motion.div>
);

const AboutPage = () => {

    const translatedStory = `Our story began in 2007, when we started quite timidly with a few employees and almost zero experience as entrepreneurs in the construction field, taking on small finishing and thermal insulation jobs. After 2 years, we managed to increase our staff numbers and thought more and more seriously about expanding our activity into structural work.\n\nGood years followed, but with a lot of work and over 100 projects implemented for other developers. Working hard and seeing the opportunities in the field we operate in, we took the next step towards becoming developers ourselves. Thus, in 2015, we began the construction of two duplexes (this time with accumulated execution experience) in Florești, a first project we remember fondly.\n\nAfterwards, larger and smaller projects followed in quite rapid succession in various locations, mostly in Florești. The last one was also the largest, a collective housing building (60 apartments) located near the Volvo-BMW dealership (in the western part of Cluj-Napoca).\n\nCurrently, we count 25 locations and nearly 500 built housing units.\n\nHaving our own employees, we closely monitor the execution and its quality. We place great emphasis on the relationship with each employee, and also with each subcontractor.\n\nWe differentiate ourselves through quality, promptness, adherence to promised deadlines, and especially care for the client even after the purchase.\n\nWe do things driven by passion for this field and the final satisfaction of the happy client who made a good choice, and we rely heavily on satisfied clients and recommendations coming from them.`;

    const coreValues = [
        { icon: <FaAward />, title: "Quality & Expertise", text: "Closely monitoring execution with our own experienced team and trusted partners to ensure the highest standards." },
        { icon: <FaClock />, title: "Reliability & Promptness", text: "Differentiating ourselves by adhering to promised deadlines and delivering projects on time." },
        { icon: <FaHandshake />, title: "Client Care", text: "Prioritizing client satisfaction throughout the process and providing dedicated support even after the purchase." },
        { icon: <FaHeart />, title: "Passion & Dedication", text: "Driven by a genuine passion for construction and the satisfaction of delivering homes clients love." },
    ];

    return (
        <div className="bg-[#eceeF1] min-h-screen">
            {/* Hero Section */}
            <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                className="pt-32 pb-16 bg-gradient-to-r from-arcdom-navy via-arcdom-dark-gray to-arcdom-navy text-center"
            >
                <div className="container mx-auto px-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">About Arcdom</h1>
                    <p className="text-lg text-arcdom-taupe max-w-2xl mx-auto">From humble beginnings to trusted developer.</p>
                </div>
            </motion.div>

            {/* Story Content Section */}
            <section className="py-16 md:py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Text Content - Changed to animate on mount */}
                        <motion.div
                            initial="hidden" // Use variant name
                            animate="visible" // Use variant name
                            variants={textBlockAnimation} // Apply the new variant
                            className="text-center lg:text-left"
                        >
                            <h2 className="text-3xl font-bold text-arcdom-navy mb-5 inline-block lg:inline">The Arcdom Journey</h2>
                            <motion.div
                                initial="hidden" // Use variant name
                                animate="visible" // Use variant name
                                variants={underlineAnimation} // Apply the new variant
                                className="h-1 bg-arcdom-gold mb-8 mx-auto lg:mx-0"
                            ></motion.div>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line text-left">
                                {translatedStory}
                            </p>
                        </motion.div>

                        {/* Lottie Animation - Can remain whileInView */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
                            className="flex justify-center items-center mt-10 lg:mt-0"
                        >
                            {storyAnimationUrl && storyAnimationUrl.includes('.lottie') && (
                                <DotLottieReact
                                    src={storyAnimationUrl}
                                    loop
                                    autoplay
                                    style={{ width: '90%', maxWidth: '750px', height: 'auto' }}
                                />
                            )}
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Values Section - whileInView is appropriate here */}
            <section className="py-16 md:py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <motion.div
                        className="text-center mb-12 md:mb-16"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.3 }}
                        variants={fadeIn}
                    >
                        <h2 className="text-3xl font-bold text-arcdom-navy mb-4">Our Core Values</h2>
                        <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: "5rem" }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="h-1 bg-arcdom-gold mx-auto mb-6"
                        ></motion.div>
                        <p className="max-w-xl mx-auto text-gray-600">
                            These principles guide every project we undertake.
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {coreValues.map((value, index) => (
                            <ValueCard key={index} icon={value.icon} title={value.title} text={value.text} />
                        ))}
                    </motion.div>
                </div>
            </section>

        </div>
    );
};

export default AboutPage;
